export type BlogSection = {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
};

export type BlogPost = {
  slug: string;
  title: string;
  description: string;
  date: string;
  readTime: string;
  audience: string;
  sections: BlogSection[];
};

export const appStoreUrl = 'https://apps.apple.com/mg/app/meal-planning-with-dinnerbrain/id6748664644';
export const webAppUrl = 'https://dinner-brain.com';

export const blogPosts: BlogPost[] = [
  {
    slug: 'summer-meal-planning-without-the-headache',
    title: 'Summer Meal Planning Without the Headache',
    description: 'A lighter way to plan summer dinners when the week is hot, loose, and allergic to perfect schedules.',
    date: '2026-06-17',
    readTime: '5 min read',
    audience: 'Busy households with unpredictable summer weeks',
    sections: [
      {
        heading: 'Summer changes the dinner problem',
        paragraphs: [
          'Summer dinner has its own personality: late sunsets, hot kitchens, camp schedules, travel weeks, long weekends, and a general sense that nobody wants a heavy plan bossing them around.',
          'That does not mean dinner gets easier. It just means the usual seven-day meal plan can start feeling too rigid for the season.',
          'A better summer dinner system is smaller, cooler, and easier to move around.'
        ]
      },
      {
        heading: 'Start with recipes your household already trusts',
        paragraphs: [
          'Summer is not the best time to build an ambitious new recipe identity. It is the time to make your reliable meals easier to find when everyone is hungry and the house is warm.',
          'Look for the dinners that already work in your real life: sandwiches that count as dinner, big salads people actually eat, grill meals, rice bowls, pasta salads, tacos, snack plates, leftovers that taste good cold, and anything that does not turn the kitchen into a sauna.'
        ]
      },
      {
        heading: 'Make a summer dinner shelf',
        paragraphs: [
          'Instead of dumping everything into one big “dinner” pile, create a small summer-ready group of recipes. Think of it as a shelf you can reach for when the week refuses to behave.'
        ],
        bullets: [
          'Pick 10 to 12 warm-weather meals your household already likes.',
          'Tag them by real-life usefulness: no oven, grill, picnic-able, fast assembly, leftovers cold, flexible, or low-cook.',
          'Add a few pantry or freezer anchors that make those meals easier to pull off.',
          'Keep the list short enough that choosing from it still feels easy.'
        ]
      },
      {
        heading: 'Plan three or four dinners, not a perfect grid',
        paragraphs: [
          'A summer meal plan does not need to prove anything. If your week has camps, errands, pool days, late work, surprise plans, or a heat index with opinions, planning seven exact dinners can create more friction than it solves.',
          'Try choosing three or four realistic dinners instead. Add one no-oven meal, one flexible meal, one leftovers-friendly meal, and one backup that can happen with almost no effort. Let the rest of the week flex.'
        ]
      },
      {
        heading: 'Build the shopping list around flexible staples',
        paragraphs: [
          'Summer planning gets easier when the grocery list supports multiple possible dinners. Tortillas, salad greens, cooked grains, rotisserie chicken, eggs, beans, frozen vegetables, pasta, sandwich supplies, and favorite sauces can cover a lot of ground.',
          'The goal is not to buy a mountain of aspirational produce. The goal is to make future-you less likely to stand in front of the fridge and start negotiating with a cucumber.'
        ]
      },
      {
        heading: 'Keep backup dinners visible',
        paragraphs: [
          'Loose weeks need visible backups. Not emergency backups hidden in your brain. Actual backups you can find quickly when the plan changes.',
          'DinnerBrain is built for this kind of practical organization: save the recipes you already trust, tag them by the kind of night they solve, move a few into your meal plan, and let the shopping list do the remembering.'
        ]
      }
    ]
  },
  {
    slug: 'dinner-plan-for-people-who-hate-meal-planning',
    title: 'A Dinner Plan for People Who Hate Meal Planning',
    description: 'A low-pressure way to plan dinner when traditional meal planning feels like homework.',
    date: '2026-05-07',
    readTime: '4 min read',
    audience: 'Overwhelmed weeknight cooks',
    sections: [
      {
        heading: 'Meal planning can ask too much',
        paragraphs: [
          'Meal planning sounds helpful until it turns into another thing you have to manage.',
          'You sit down with good intentions, open a few recipe tabs, imagine a more organized version of yourself, and somehow end up with a plan that requires twelve ingredients you do not usually buy and a Wednesday night energy level you absolutely will not have.',
          'That is not a personal failure. That is a system asking too much.'
        ]
      },
      {
        heading: 'Try the 3-1-1 dinner plan',
        paragraphs: ['A better dinner plan can be smaller. Instead of planning every bite of the week, build around meals your household already trusts.'],
        bullets: [
          '3 dependable meals you already know how to make.',
          '1 flexible meal that can absorb leftovers, pantry odds and ends, or whatever needs using.',
          '1 intentionally easy night: leftovers, freezer food, breakfast-for-dinner, takeout, or anything that keeps the week moving.'
        ]
      },
      {
        heading: 'Plan for your actual week',
        paragraphs: [
          'The goal is not to become a different person by Thursday. It is to stop re-deciding dinner from scratch every evening.',
          'Look at the week you really have. Late meeting? Sports practice? Low-energy night? Build those constraints into the plan instead of pretending they will not happen.'
        ]
      },
      {
        heading: 'Keep the reliable meals close',
        paragraphs: [
          'A small list of dependable dinners is more useful than a giant pile of recipes you might make someday.',
          'When your meals, plan, and shopping list live together, dinner becomes less of a nightly debate and more of a quick check-in.'
        ]
      }
    ]
  },
  {
    slug: 'repeat-meal-is-not-a-failure',
    title: 'The Repeat Meal Is Not a Failure',
    description: 'Repeating dinners can reduce decision fatigue, simplify groceries, and make weeknights calmer.',
    date: '2026-05-07',
    readTime: '3 min read',
    audience: 'Busy families and budget-conscious households',
    sections: [
      {
        heading: 'Novelty is optional',
        paragraphs: [
          'The repeat meal is not a failure. It is a strategy.',
          'Somewhere along the way, weeknight dinner picked up this weird expectation that every meal should feel fresh, clever, or worth photographing. Most households do not need more novelty at 5:37 PM. They need fewer decisions.'
        ]
      },
      {
        heading: 'Reliable meals do useful work',
        bullets: [
          'They narrow the dinner choices before the day gets chaotic.',
          'They make grocery shopping easier because the ingredients are familiar.',
          'They help reduce waste because the food you buy actually has a plan.',
          'They give everyone one less thing to negotiate.'
        ]
      },
      {
        heading: 'Repeat on purpose',
        paragraphs: [
          'A reliable taco night, pasta night, soup night, rice bowl night, or breakfast-for-dinner night can carry more of the week than we give it credit for.',
          'DinnerBrain is built around that idea: keep the recipes you already know and love, plan from those, and make dinner feel manageable again.'
        ]
      }
    ]
  },
  {
    slug: 'stop-saving-recipes-everywhere',
    title: 'Stop Saving Recipes Everywhere',
    description: 'Scattered recipes make dinner harder. Here is a calmer way to organize favorite meals and turn them into plans.',
    date: '2026-05-07',
    readTime: '5 min read',
    audience: 'Home cooks with recipes scattered across too many places',
    sections: [
      {
        heading: 'The problem is not a lack of ideas',
        paragraphs: [
          'The problem is not that you have no dinner ideas. You probably have plenty of them.',
          'They are just scattered across screenshots, browser bookmarks, saved posts, notes, cookbooks, texts from family, and that one recipe you are pretty sure you emailed yourself two years ago.',
          'That kind of recipe clutter does not feel like clutter at first. It feels like being prepared. Until it is time to make dinner.'
        ]
      },
      {
        heading: 'Scattered recipes create scattered decisions',
        paragraphs: [
          'Every saved recipe becomes another place to search, another choice to weigh, and another tiny bit of friction between “we should eat” and “dinner is handled.”',
          'A useful recipe system should make your trusted meals easier to find, not turn dinner into a scavenger hunt.'
        ]
      },
      {
        heading: 'Start with meals that already work',
        bullets: [
          'Pull together the meals your household actually repeats.',
          'Label them by real-life usefulness: fast, cheap, low-energy, leftovers, kid-friendly, company-worthy.',
          'Skip the fantasy version of your week and save the recipes that fit the one you really live.'
        ]
      },
      {
        heading: 'Turn saved recipes into actual dinner plans',
        paragraphs: [
          'A saved recipe is only helpful if it can become dinner. Once your favorites are organized, the next step is simple: choose a few for the week, leave room for flexibility, and let the grocery list close the loop.',
          'That is where DinnerBrain is meant to help — one calm place for recipes, meal plans, and shopping lists.'
        ]
      }
    ]
  }
];

export const getBlogPost = (slug: string | undefined) => blogPosts.find((post) => post.slug === slug);
